/**
 * Zen bahçesi — sade arka plan müziği + ortam (Web Audio sentezi, dosya yok).
 * Katmanlar: Yo pentatonik yumuşak koto/kalimba notaları (6–11 sn'de bir 1–3 nota), seyrek pad akoru,
 * seyrek kuş, su damlası "plink", ara sıra bambu "tok" (shishi-odoshi), çok hafif alçak rüzgâr.
 * Tiz gürültü yok (eski yaprak hışırtısı "poşet" gibi duyuluyordu).
 *
 * Olaylar ses saatiyle planlanır: schedule(until) o ana kadarki olayları kurar. Canlıda Sounds.js
 * yarım saniyede bir ileriye doğru çağırır; çevrimdışı ölçümde bir kerede tüm süre için çağrılır.
 */
const SCALE = [293.66, 329.63, 392.0, 440.0, 493.88, 587.33, 659.25, 783.99] // D4 E4 G4 A4 B4 D5 E5 G5
const PADS = [[146.83, 220.0, 329.63], [196.0, 293.66, 440.0], [164.81, 246.94, 392.0]] // Dsus2, Gsus2, Em7 gövdesi

export default class ZenGarden
{
    constructor(_context, _destination)
    {
        this.context = _context
        this.stats = { notes: 0, pads: 0, birds: [], drops: 0, bamboo: 0 }

        this.output = _context.createGain()
        this.output.gain.value = 0.7 // ölçüm (ana düzey 0.5): tepe ≈ -19 dBFS, RMS ≈ -36 dBFS — sade, arka planda
        this.output.connect(_destination)

        // Yankı: sentez dürtü yanıtı (3 sn, yumuşak sönen stereo gürültü)
        this.reverb = _context.createConvolver()
        this.reverb.buffer = this.impulse(3.2)
        this.wet = _context.createGain()
        this.wet.gain.value = 0.5
        this.reverb.connect(this.wet)
        this.wet.connect(this.output)

        this.dry = _context.createGain()
        this.dry.gain.value = 0.7
        this.dry.connect(this.output)

        this.setWind()

        const now = _context.currentTime
        this.next = { note: now + 1.5, pad: now + 0.5, bird: now + 4, drop: now + 3, bamboo: now + 18 }
    }

    impulse(_seconds)
    {
        const length = Math.floor(this.context.sampleRate * _seconds)
        const buffer = this.context.createBuffer(2, length, this.context.sampleRate)
        for(let channel = 0; channel < 2; channel++)
        {
            const data = buffer.getChannelData(channel)
            let smooth = 0
            for(let i = 0; i < length; i++)
            {
                // yumuşatılmış gürültü (tizleri kısar) × üstel sönüm
                smooth += ((Math.random() * 2 - 1) - smooth) * 0.35
                data[i] = smooth * Math.pow(1 - i / length, 3.2)
            }
        }
        return buffer
    }

    setWind()
    {
        // Çok hafif, alçak (≤ 300 Hz) esinti: döngü sonu başa çapraz geçişle bağlanır
        const context = this.context
        const seconds = 10
        const length = Math.floor(context.sampleRate * seconds)
        const fade = Math.floor(context.sampleRate * 0.5)
        const raw = new Float32Array(length + fade)
        let last = 0
        for(let i = 0; i < raw.length; i++)
        {
            last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02
            raw[i] = last * 3 * (0.75 + 0.25 * Math.sin(i / context.sampleRate * 0.7))
        }
        const buffer = context.createBuffer(1, length, context.sampleRate)
        const data = buffer.getChannelData(0)
        for(let i = 0; i < length; i++)
        {
            data[i] = i < fade ? raw[i] * (i / fade) + raw[length + i] * (1 - i / fade) : raw[i]
        }
        const source = context.createBufferSource()
        source.buffer = buffer
        source.loop = true
        const filter = context.createBiquadFilter()
        filter.type = 'lowpass'
        filter.frequency.value = 300
        const gain = context.createGain()
        gain.gain.value = 0.16
        source.connect(filter)
        filter.connect(gain)
        gain.connect(this.output)
        source.start()
    }

    envelope(_gain, _time, _peak, _attack, _decay)
    {
        _gain.gain.setValueAtTime(0.0001, _time)
        _gain.gain.exponentialRampToValueAtTime(_peak, _time + _attack)
        _gain.gain.exponentialRampToValueAtTime(0.0001, _time + _attack + _decay)
    }

    // Koto / kalimba benzeri yumuşak telli: üçgen + 2. ve 3. harmonik, uzun sönüm
    note(_frequency, _time, _velocity)
    {
        const context = this.context
        const gain = context.createGain()
        const filter = context.createBiquadFilter()
        filter.type = 'lowpass'
        filter.frequency.value = 2200
        gain.connect(filter)
        filter.connect(this.dry)
        filter.connect(this.reverb)
        this.envelope(gain, _time, 0.22 * _velocity, 0.008, 2.8)

        for(const [_type, _ratio, _level] of [['triangle', 1, 1], ['sine', 2, 0.22], ['sine', 3.01, 0.07]])
        {
            const oscillator = context.createOscillator()
            oscillator.type = _type
            oscillator.frequency.value = _frequency * _ratio
            const level = context.createGain()
            level.gain.value = _level
            oscillator.connect(level)
            level.connect(gain)
            oscillator.start(_time)
            oscillator.stop(_time + 3)
        }
        this.stats.notes++
    }

    pad(_chord, _time)
    {
        const context = this.context
        const gain = context.createGain()
        const filter = context.createBiquadFilter()
        filter.type = 'lowpass'
        filter.frequency.value = 800
        gain.connect(filter)
        filter.connect(this.dry)
        filter.connect(this.reverb)
        gain.gain.setValueAtTime(0, _time)
        gain.gain.linearRampToValueAtTime(0.045, _time + 6)
        gain.gain.setValueAtTime(0.045, _time + 9)
        gain.gain.linearRampToValueAtTime(0, _time + 17)

        for(const _frequency of _chord)
        {
            for(const _detune of [- 5, 5])
            {
                const oscillator = context.createOscillator()
                oscillator.type = 'triangle'
                oscillator.frequency.value = _frequency
                oscillator.detune.value = _detune
                oscillator.connect(gain)
                oscillator.start(_time)
                oscillator.stop(_time + 17.1)
            }
        }
        this.stats.pads++
    }

    bird(_time)
    {
        const context = this.context
        const panner = context.createStereoPanner ? context.createStereoPanner() : null
        const out = context.createGain()
        out.gain.value = 0.18
        if(panner)
        {
            panner.pan.value = Math.random() * 1.2 - 0.6
            out.connect(panner)
            panner.connect(this.dry)
        }
        else
        {
            out.connect(this.dry)
        }
        out.connect(this.reverb)

        const base = 2600 + Math.random() * 1600
        const notes = 2 + Math.floor(Math.random() * 3)
        let time = _time
        for(let i = 0; i < notes; i++)
        {
            const duration = 0.06 + Math.random() * 0.08
            const oscillator = context.createOscillator()
            const gain = context.createGain()
            oscillator.frequency.setValueAtTime(base * (0.85 + Math.random() * 0.2), time)
            oscillator.frequency.exponentialRampToValueAtTime(base * (1.15 + Math.random() * 0.3), time + duration)
            this.envelope(gain, time, 0.5, 0.012, duration)
            oscillator.connect(gain)
            gain.connect(out)
            oscillator.start(time)
            oscillator.stop(time + duration + 0.05)
            time += duration + 0.04 + Math.random() * 0.08
        }
        this.stats.birds.push([_time, time + 0.1])
    }

    // Su damlası: yukarı kayan kısa sinüs (gerçek damla sesi yukarı "plink" yapar), çoğu yankıya
    drop(_time)
    {
        const context = this.context
        const oscillator = context.createOscillator()
        const gain = context.createGain()
        const start = 650 + Math.random() * 300
        oscillator.frequency.setValueAtTime(start, _time)
        oscillator.frequency.exponentialRampToValueAtTime(start * 2.1, _time + 0.03)
        this.envelope(gain, _time, 0.09, 0.004, 0.09)
        oscillator.connect(gain)
        gain.connect(this.reverb)
        gain.connect(this.dry)
        oscillator.start(_time)
        oscillator.stop(_time + 0.15)
        this.stats.drops++
    }

    // Bambu "tok": iki kısa sinüs kısmi (tahta rezonansı)
    bamboo(_time)
    {
        const context = this.context
        const gain = context.createGain()
        gain.connect(this.dry)
        gain.connect(this.reverb)
        this.envelope(gain, _time, 0.3, 0.002, 0.14)
        for(const [_frequency, _level] of [[470, 1], [1130, 0.35]])
        {
            const oscillator = context.createOscillator()
            oscillator.frequency.value = _frequency
            const level = context.createGain()
            level.gain.value = _level
            oscillator.connect(level)
            level.connect(gain)
            oscillator.start(_time)
            oscillator.stop(_time + 0.2)
        }
        this.stats.bamboo++
    }

    // _until (ses saati) anına kadarki olayları kur
    schedule(_until)
    {
        const random = (_min, _max) => _min + Math.random() * (_max - _min)
        const next = this.next

        while(next.note < _until)
        {
            // 1–3 notalık cümle: dizide küçük adımlarla gezinir
            let index = Math.floor(Math.random() * SCALE.length)
            let time = next.note
            const count = 1 + Math.floor(Math.random() * 3)
            for(let i = 0; i < count; i++)
            {
                this.note(SCALE[index], time, 0.6 + Math.random() * 0.4)
                index = Math.min(SCALE.length - 1, Math.max(0, index + [- 2, - 1, 1, 2][Math.floor(Math.random() * 4)]))
                time += random(0.45, 0.9)
            }
            next.note = time + random(6, 11)
        }
        while(next.pad < _until)
        {
            this.pad(PADS[Math.floor(Math.random() * PADS.length)], next.pad)
            next.pad += random(22, 32)
        }
        while(next.bird < _until)
        {
            this.bird(next.bird)
            next.bird += random(7, 15)
        }
        while(next.drop < _until)
        {
            this.drop(next.drop)
            next.drop += random(4, 9)
        }
        while(next.bamboo < _until)
        {
            this.bamboo(next.bamboo)
            next.bamboo += random(28, 45)
        }
    }

    // Sekme uzun süre gizli kaldıysa geçmişe olay yığmamak için planı şimdiye çek
    catchUp(_now)
    {
        for(const _key in this.next)
        {
            this.next[_key] = Math.max(this.next[_key], _now + 0.2)
        }
    }
}
